import React, {useState, useEffect} from "react"
import UserSearch from "@irati/ui/src/UserSearch"
import {H2} from "@irati/ui/src/Typography"
import {Row, Col} from "@irati/ui/src/Layout"
import Papa from "papaparse"
import moment from "moment-timezone"
import {Select} from "@irati/ui/src/FormItem"
import FormattedMessage, {useIntl} from "@irati/utils/lang"
import {ListCustomRewardsTransactionsFilter, User, CustomRewardTransaction} from "@irati/types"
import Spacing from "@irati/ui/src/Spacing"
import firstnameLastname from "@irati/identity/helpers/user/firstnameLastname"
import {useDownloadCustomRewardTransactionsQuery} from "@irati/custom-reward/hooks"
import makeCSV from "@irati/utils/csv/makeCSV"
import {FormatMessage} from "@irati/utils/lang/types"
import CustomRewardsTransactions from "@irati-web/wallet/src/components/Transactions/CustomRewardsTransactions"
import Flex from "@irati/ui/src/Flex"
import Button from "@irati/ui/src/Button"
import Error from "@irati/ui/src/Loading/Error"
import {IconTypes} from "@irati/ui/src/Icon"
enum CustomRewardStatusUsage {
  USED,
  NOT_USED,
  USED_UNUSED
}
const CSV_FIELDS = [
  {
    title: "admin.customRewards.csv.header.recipient",
    getValue: (transaction: CustomRewardTransaction) => firstnameLastname(transaction.receiver)
  },
  {
    title: "admin.customRewards.csv.header.recipientEmail",
    getValue: (transaction: CustomRewardTransaction) => transaction.receiver?.email ?? ""
  },
  {
    title: "admin.customRewards.csv.header.sender",
    getValue: (transaction: CustomRewardTransaction) => firstnameLastname(transaction.author)
  },
  {
    title: "admin.customRewards.csv.header.senderEmail",
    getValue: (transaction: CustomRewardTransaction) => transaction.author?.email ?? ""
  },
  {
    title: "admin.customRewards.csv.header.reward",
    getValue: (transaction: CustomRewardTransaction) => transaction.customReward.title
  },
  {
    title: "admin.customRewards.csv.header.issuedOn",
    getValue: (transaction: CustomRewardTransaction) => moment(transaction.metadata.createdAt).format("DD/MMMM/YYYY")
  },
  {
    title: "admin.customRewards.csv.header.redeemed",
    getValue: (transaction: CustomRewardTransaction, formatMessage: FormatMessage) =>
      formatMessage({id: transaction.isUsed ? "admin.customRewards.csv.cell.isRedeemed" : "admin.customRewards.csv.cell.notRedeemed"})
  }
]
type UsageTypeOption = {
  value: CustomRewardStatusUsage
  label: string
}
const getUsageFilterOptions = (formatMessage: FormatMessage): UsageTypeOption[] => [
  {
    value: CustomRewardStatusUsage.USED_UNUSED,
    label: formatMessage({id: "admin.search.option.usedUnused"})
  },
  {
    value: CustomRewardStatusUsage.USED,
    label: formatMessage({id: "admin.search.option.used"})
  },
  {
    value: CustomRewardStatusUsage.NOT_USED,
    label: formatMessage({id: "admin.search.option.unused"})
  }
]
const getIsUsedQueryArg = (selectedOption: UsageTypeOption): boolean | null => {
  if (selectedOption.value === CustomRewardStatusUsage.USED_UNUSED) {
    return null
  }
  return selectedOption.value === CustomRewardStatusUsage.USED
}
const CustomRewardsListTransactions = () => {
  const {formatMessage} = useIntl()
  const usageFilterOptions = getUsageFilterOptions(formatMessage)
  const [receiver, setReceiver] = useState<User>()
  const [usageFilter, setUsageFilter] = useState<UsageTypeOption>(usageFilterOptions[0])
  const [downloadAllTransactions, {loading, data, error}] = useDownloadCustomRewardTransactionsQuery(null)
  const downloadTransactions = async () => {
    if (!data) return
    const csvData = data?.map((transaction) => {
      return CSV_FIELDS.map((field) => {
        return field.getValue(transaction, formatMessage)
      })
    })
    const headers = CSV_FIELDS.map(({title}) => formatMessage({id: title}))
    const csv = Papa.unparse({fields: headers, data: csvData})
    makeCSV(csv, "transactions.csv")
  }
  useEffect(() => {
    if (!data) return
    downloadTransactions()
  }, [data])
  const handleReceiverChange = (_, user: User) => {
    setReceiver(user)
  }
  const onSelectedUsageFilter = (newUsageFilter: CustomRewardStatusUsage) => {
    setUsageFilter(usageFilterOptions.find(({value}) => value === newUsageFilter))
  }
  const queryFilters: ListCustomRewardsTransactionsFilter = {
    receiverId: receiver?.uuid,
    isUsed: getIsUsedQueryArg(usageFilter)
  }
  return (
    <div>
      <Flex alignItems="flex-start" justifyContent="space-between">
        <H2 marginBottom={4}>
          <FormattedMessage id="admin.customRewards.heading.transactions" />
        </H2>
        <Flex gap={2} direction="column">
          <Button onClick={() => downloadAllTransactions()} loading={loading} cta icon={IconTypes.downloadReport}>
            <FormattedMessage id="admin.customRewards.btn.downloadTransactions" />
          </Button>
          {error && <Error error={error} errorMsg={error.toString()}></Error>}
        </Flex>
      </Flex>
      <Row>
        <Col xs={6}>
          <Row>
            <UserSearch
              onChange={handleReceiverChange}
              isClearable
              value={receiver}
              flush
              hideLabel
              placeholder="platform.fields.emplyeeSearch.placeholder"
            />
          </Row>
        </Col>
        <Col xs={6}>
          <Select
            id="selectUsageFilter"
            isClearable={false}
            isSearchable={false}
            options={usageFilterOptions}
            hideLabel
            flush
            name="selectUsageFilter"
            onChange={onSelectedUsageFilter}
            value={usageFilter}
          />
        </Col>
      </Row>
      <Spacing marginTop={4}>
        <CustomRewardsTransactions filters={queryFilters} isRedeemable />
      </Spacing>
    </div>
  )
}

export default CustomRewardsListTransactions
